import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetReduxState } from '../actions';

interface FinancialStatementItem {
  id: string;
  name: string;
  amount: string;
  isImmaterial: boolean;
  cycles: any[];
  processes: any[];
  index: number;
  createdAt: string;
}

interface FinancialStatementState {
  // Data for each section
  assetsData: FinancialStatementItem[];
  liabilitiesData: FinancialStatementItem[];
  equityData: FinancialStatementItem[];
  revenueData: FinancialStatementItem[];
  expensesData: FinancialStatementItem[];
  
  // Metadata
  lastFetchedReportId: string | null;
  isFetched: boolean;
}

const initialState: FinancialStatementState = {
  assetsData: [],
  liabilitiesData: [],
  equityData: [],
  revenueData: [],
  expensesData: [],
  lastFetchedReportId: null,
  isFetched: false,
};

const financialStatementSlice = createSlice({
  name: 'financialStatement',
  initialState,
  reducers: {
    setAssetsData: (state, action: PayloadAction<FinancialStatementItem[]>) => {
      state.assetsData = action.payload;
    },
    setLiabilitiesData: (state, action: PayloadAction<FinancialStatementItem[]>) => {
      state.liabilitiesData = action.payload;
    },
    setEquityData: (state, action: PayloadAction<FinancialStatementItem[]>) => {
      state.equityData = action.payload;
    },
    setRevenueData: (state, action: PayloadAction<FinancialStatementItem[]>) => {
      state.revenueData = action.payload;
    },
    setExpensesData: (state, action: PayloadAction<FinancialStatementItem[]>) => {
      state.expensesData = action.payload;
    },
    setAllFinancialData: (
      state,
      action: PayloadAction<{
        assetsData: FinancialStatementItem[];
        liabilitiesData: FinancialStatementItem[];
        equityData: FinancialStatementItem[];
        revenueData: FinancialStatementItem[];
        expensesData: FinancialStatementItem[];
        reportId: string;
      }>
    ) => {
      state.assetsData = action.payload.assetsData;
      state.liabilitiesData = action.payload.liabilitiesData;
      state.equityData = action.payload.equityData;
      state.revenueData = action.payload.revenueData;
      state.expensesData = action.payload.expensesData;
      state.lastFetchedReportId = action.payload.reportId;
      state.isFetched = true;
    },
    clearFinancialData: (state) => {
      state.assetsData = [];
      state.liabilitiesData = [];
      state.equityData = [];
      state.revenueData = [];
      state.expensesData = [];
      state.lastFetchedReportId = null;
      state.isFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setAssetsData,
  setLiabilitiesData,
  setEquityData,
  setRevenueData,
  setExpensesData,
  setAllFinancialData,
  clearFinancialData,
} = financialStatementSlice.actions;

export default financialStatementSlice.reducer;

